import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Contact } from '~api/address_book';

import { AddressBookApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';

interface AddressBookListQueryParams {
  order_by_direction?: 'ASCENDING' | 'DESCENDING';
  pageIndex?: number;
  pageSize?: number;
  query?: string;
}

export function useAddressBookQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['addressBook', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (filters: AddressBookListQueryParams) => [...keys.lists(), filters] as const,
    details: () => [...keys.all, 'detail'] as const,
    detail: (id?: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useAddressBookDetailsQuery({ id }: Pick<Contact, 'id'>) {
  const queryKey = useAddressBookQueryKey();
  const siteId = useSiteId();

  return useSuspenseQuery({
    queryFn: async () => {
      const { data } = await AddressBookApiInstance.addressBookGetContact({ id, site_id: siteId });
      return data;
    },
    queryKey: queryKey.detail(id),
  });
}

export function useAddressBookListQuery(params: AddressBookListQueryParams, enabled = true) {
  const queryKey = useAddressBookQueryKey();
  const siteId = useSiteId();

  const DEFAULT_PAGE_INDEX = 0;
  const DEFAULT_PAGE_SIZE = 10;

  return useQuery({
    queryFn: async () => {
      const { data } = await AddressBookApiInstance.addressBookListContacts({
        order_by_direction: params.order_by_direction,
        'pagination.limit': params.pageSize ?? DEFAULT_PAGE_SIZE,
        'pagination.offset': (params.pageIndex ?? DEFAULT_PAGE_INDEX) * (params.pageSize ?? DEFAULT_PAGE_SIZE),
        query: params.query,
        site_id: siteId,
      });

      return data;
    },
    queryKey: queryKey.list(params),
    enabled,
  });
}
